/**
 * Maps Stripe.js / API errors to short user-facing top-up messages.
 * Avoids exposing developer terms like payment_method_data.
 */

const normalize = (value) => String(value || "").toLowerCase().trim();

const isAuthenticationCancelled = (code, message) =>
  message.includes("cancel") ||
  message.includes("canceled") ||
  message.includes("cancelled");

const isAuthenticationFailure = (code, message) => {
  if (isAuthenticationCancelled(code, message)) {
    return false;
  }
  if (code === "payment_intent_authentication_failure") {
    return true;
  }
  return (
    message.includes("failed authentication") ||
    message.includes("payment_method_data") ||
    message.includes("unable to authenticate")
  );
};

export const getStripeTopUpUserMessage = (error, fallback = "Payment failed. Please try again.") => {
  if (!error) {
    return fallback;
  }

  if (typeof error === "string") {
    return getStripeTopUpUserMessage({ message: error }, fallback);
  }

  const code = normalize(error.code || error?.response?.data?.code);
  const declineCode = normalize(error.decline_code);
  const message = normalize(
    error.message ||
      error?.response?.data?.message ||
      error?.response?.data?.error ||
      ""
  );

  if (isAuthenticationCancelled(code, message)) {
    return "Payment verification was cancelled. No charge was made.";
  }

  if (isAuthenticationFailure(code, message)) {
    return "Card authentication failed. Please try again or use a different card.";
  }

  if (code === "card_declined") {
    if (declineCode === "insufficient_funds") {
      return "Your card has insufficient funds. Please try another card.";
    }
    if (declineCode === "authentication_required") {
      return "Your bank requires extra verification. Please try again and complete the authentication step.";
    }
    return "Your card was declined. Please try another card or contact your bank.";
  }

  if (code === "expired_card" || message.includes("expired")) {
    return "This card has expired. Please use a different card.";
  }

  if (code === "processing_error") {
    return "A temporary processing error occurred. Please try again in a moment.";
  }

  if (message.includes("insufficient funds")) {
    return "Your card has insufficient funds. Please try another card.";
  }

  if (message.includes("payment_method") && message.includes("not valid")) {
    return "Selected payment method is not valid. Please choose a card again.";
  }

  // Never show raw Stripe developer messages to users.
  if (
    message.includes("payment_method_data") ||
    message.includes("paymentintent") ||
    message.includes("payment_intent")
  ) {
    return "Payment could not be completed. Please try again or use a different card.";
  }

  return fallback;
};
